import { ReactNode } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { BsFillDiamondFill, BsFillShieldFill } from "react-icons/bs";
import { FaMagic, FaMobile, FaVideo, FaRegLightbulb } from "react-icons/fa";
import { IoCheckmark } from "react-icons/io5";
import { RiVipDiamondFill } from "react-icons/ri";
import {
  BiSolidAlarmExclamation,
  BiSolidMessageSquareEdit,
} from "react-icons/bi";
import { MdDownload, MdAllInclusive, MdWaterDrop } from "react-icons/md";
import { useSetRecoilState } from "recoil";

import { Button } from "../../Button";
import { NAV_LINKS } from "../../Sidebar/utils";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { useTrialEligibility } from "MVVM/Hooks/useTrialEligibility";
import { StripePaystationState } from "MVVM/State";
import {
  TIER_DATA,
  TIER_HIGHLIGHTS,
} from "MVVM/Views/Pricing/Components/PricingCards/features";
import {
  IHighlightItem,
  IHighlightSection,
} from "MVVM/Views/Pricing/Components/PricingCards/types";
import { analytics } from "MVVM/Analytics";
import { TIER_PRICING } from "MVVM/Utilities/SubscriptionTiers";

const { REACT_APP_S3_CLIP_BUCKET } = process.env;

type TUpgradeTier = "ESSENTIALS" | "PRO" | "CREATOR_PLAT";

interface ITierItem {
  icon: ReactNode;
  text: string;
}

const formatHighlight = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
};

const TrialCard = () => {
  const setStripePaystation = useSetRecoilState(StripePaystationState);
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const { requiresCard } = useTrialEligibility();
  const tierData = TIER_DATA.find((t) => t.key === "CREATOR_PLAT");
  const sections = TIER_HIGHLIGHTS["CREATOR_PLAT"]?.sections || [];

  if (!tierData) return <></>;

  const tierName = showSubscriptionOverhaul ? "Unlimited" : "Platinum";

  const handleStartTrial = () => {
    analytics.track("Dropdown Trial CTA Clicked");
    setStripePaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      lookUp: tierData.stripeLookup.Monthly,
      trial: true,
    });
  };

  return (
    <TrialCardContainer>
      <TrialHeader>
        <TrialBadge>7-DAY FREE TRIAL</TrialBadge>
        <TrialHeading>
          Try <TrialAccent>{tierName}</TrialAccent> Free
        </TrialHeading>
        <TrialPriceSubtext>
          {requiresCard
            ? `Then ${tierData.pricing.monthly}/mo · Cancel anytime`
            : "No card required · Cancel anytime"}
        </TrialPriceSubtext>
      </TrialHeader>
      <TrialBody>
        {sections.slice(0, 1).map((section: IHighlightSection, si: number) => (
          <TrialFeatureGroup key={si}>
            <TrialSectionTitle>{section.title}</TrialSectionTitle>
            {section.items.map((item: string | IHighlightItem, ii: number) => {
              const text = typeof item === "object" ? item.text : item;
              return (
                <TrialFeatureItem key={ii}>
                  <TrialCheckIcon />
                  <TrialFeatureText>{formatHighlight(text)}</TrialFeatureText>
                </TrialFeatureItem>
              );
            })}
          </TrialFeatureGroup>
        ))}
        <TrialCTAButton onClick={handleStartTrial} data-close="true">
          Start free trial
        </TrialCTAButton>
      </TrialBody>
    </TrialCardContainer>
  );
};

const UpgradeCard = ({ currentTier }: { currentTier?: string }) => {
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const { isTrialEligible } = useTrialEligibility();

  const tierData = {
    ESSENTIALS: {
      title: showSubscriptionOverhaul ? "Allstar Basic" : "Allstar Essentials",
      buttonText: showSubscriptionOverhaul ? "Get Basic" : "Get Essentials",
      price: `${TIER_PRICING.ESSENTIALS.monthly}/month`,
      perks: [
        {
          text: showSubscriptionOverhaul ? "Basic Badge" : "Essentials Badge",
          icon: <StyledBsFillShieldFill />,
        },
        { text: "Unlimited Clips", icon: <StyledFaVideo /> },
        { text: "Unlimited Remixes", icon: <StyledFaMagic /> },
      ],
    },
    PRO: {
      title: showSubscriptionOverhaul ? "Allstar Standard" : "Allstar Pro",
      buttonText: showSubscriptionOverhaul ? "Get Standard" : "Get Pro",
      price: `${TIER_PRICING.PRO.monthly}/month`,
      perks: [
        {
          text: showSubscriptionOverhaul ? "Standard Badge" : "Pro Badge",
          icon: <StyledBsFillShieldFill />,
        },
        { text: "Premium Creator Cards", icon: <StyledBsFillDiamondFill /> },
        {
          text: "Ad-Free Experience",
          icon: <StyledBiSolidAlarmExclamation />,
        },
        { text: "Remove Watermarks", icon: <StyledMdWaterDrop /> },
        { text: "Username Changes", icon: <StyledBiSolidMessageSquareEdit /> },
        {
          text: "Early Access Features",
          icon: <StyledFaRegLightbulb />,
        },
      ],
    },
    CREATOR_PLAT: {
      title: showSubscriptionOverhaul
        ? "Allstar Unlimited"
        : "Allstar Platinum",
      buttonText: showSubscriptionOverhaul ? "Get Unlimited" : "Get Platinum",
      price: `${TIER_PRICING.CREATOR_PLAT.monthly}/month`,
      perks: [
        {
          text: showSubscriptionOverhaul ? "Unlimited Badge" : "Platinum Badge",
          icon: <StyledBsFillShieldFill />,
        },
        { text: "Legendary Creator Cards", icon: <StyledRiVipDiamondFill /> },
        { text: "Automatic 9:16 Clips", icon: <StyledFaMobile /> },
        {
          text: showSubscriptionOverhaul
            ? "Unlimited Concierge Service"
            : "Platinum Concierge Service",
          icon: <StyledBiSolidMessageSquareEdit />,
        },
      ],
    },
  };

  if (!currentTier) return <></>;

  if (isTrialEligible) return <TrialCard />;

  const nextTier = (function () {
    switch (currentTier) {
      case "ESSENTIALS":
        return "PRO";
      case "TRIAL":
      case "PRO":
      case "PRO_PLUS":
        return "CREATOR_PLAT";
      default:
        return;
    }
  })();

  if (!nextTier) return <></>;

  return (
    <StyledCard tier={nextTier}>
      <Text>Unlock the next tier:</Text>
      <Text color="ice" fontSize={7} fontWeight={700}>
        {tierData[nextTier].title}
      </Text>
      <Text color="ice" fontWeight={500}>
        {tierData[nextTier].price}
      </Text>
      <PerksSection>
        {tierData[nextTier]?.perks.map((item: ITierItem, index: number) => (
          <ListItem key={index}>
            {item.icon}
            <Text>{item.text}</Text>
          </ListItem>
        ))}
      </PerksSection>
      <StyledButton
        size="small"
        mt={2}
        tier={nextTier}
        to={NAV_LINKS.upgrade}
        data-close="true"
      >
        {tierData[nextTier].buttonText}
      </StyledButton>
    </StyledCard>
  );
};

const PerksSection = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  margin: 20px 0;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    display: none;
  }
`;

const iconStyle = css`
  color: ${({ theme }) => theme.colors.ice};
`;

const platinumStyle = css`
  background-image: linear-gradient(130.44deg, #1394c8 6.35%, #824cb0 83.2%),
    url(${REACT_APP_S3_CLIP_BUCKET}/static/web/brand/upgradeLogo.png);
`;

const proStyle = css`
  background-image: url(${REACT_APP_S3_CLIP_BUCKET}/static/web/brand/upgradeLogo.png),
    linear-gradient(227.35deg, #1278a1 -4.11%, #4dbd94 100%);
`;

const essentialsStyle = css`
  background-image: url(${REACT_APP_S3_CLIP_BUCKET}/static/web/brand/upgradeLogo.png),
    linear-gradient(227.35deg, #5fda9f -4.11%, #2d9f6b 100%);
`;

const StyledButton = styled(Button)<{ tier: TUpgradeTier }>`
  background-color: ${({ theme }) => theme.colors.ice};
  color: ${(props) =>
    props.tier === "ESSENTIALS" ? props.theme.colors.premium : ""};
  color: ${(props) => (props.tier === "PRO" ? props.theme.colors.premium : "")};
  color: ${(props) =>
    props.tier === "CREATOR_PLAT" ? props.theme.colors.legendary : ""};

  :hover {
    background-color: ${({ theme }) => theme.colors.iceAlpha75};
  }
`;

const ListItem = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 500;
  gap: 10px;
`;

const StyledFaMobile = styled(FaMobile)`
  ${iconStyle}
`;

const StyledFaMagic = styled(FaMagic)`
  ${iconStyle}
`;

const StyledFaVideo = styled(FaVideo)`
  ${iconStyle}
`;

const StyledMdDownload = styled(MdDownload)`
  ${iconStyle}
`;

const StyledMdWaterDrop = styled(MdWaterDrop)`
  ${iconStyle}
`;

const StyledFaRegLightbulb = styled(FaRegLightbulb)`
  ${iconStyle}
`;

const StyledMdAllInclusive = styled(MdAllInclusive)`
  ${iconStyle}
`;

const StyledBsFillDiamondFill = styled(BsFillDiamondFill)`
  ${iconStyle}
`;

const StyledBsFillShieldFill = styled(BsFillShieldFill)`
  ${iconStyle}
`;

const StyledRiVipDiamondFill = styled(RiVipDiamondFill)`
  ${iconStyle}
`;

const StyledBiSolidAlarmExclamation = styled(BiSolidAlarmExclamation)`
  ${iconStyle}
`;

const StyledBiSolidMessageSquareEdit = styled(BiSolidMessageSquareEdit)`
  ${iconStyle}
`;

const StyledCard = styled(Box)<{ tier: TUpgradeTier }>`
  ${(props) => (props.tier === "ESSENTIALS" ? essentialsStyle : "")};
  ${(props) => (props.tier === "PRO" ? proStyle : "")};
  ${(props) => (props.tier === "CREATOR_PLAT" ? platinumStyle : "")};

  background-position: right top;
  background-repeat: no-repeat;
  border-radius: 12px;
  margin-bottom: 16px;
  padding: 20px;
`;

const TrialCardContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid rgba(154, 88, 209, 0.2);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const TrialHeader = styled(Box)`
  background: linear-gradient(
    135deg,
    rgba(154, 88, 209, 0.25) 0%,
    transparent 60%
  );
  padding: 16px;
`;

const TrialBody = styled(Box)`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px 16px;
`;

const TrialBadge = styled(Text)`
  background: rgba(154, 88, 209, 0.35);
  border-radius: 20px;
  color: white;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 3px 10px;
  width: fit-content;
`;

const TrialHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 18px;
  font-weight: 700;
  margin-top: 6px;
`;

const TrialAccent = styled.span`
  color: #9a58d1;
`;

const TrialPriceSubtext = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  margin-top: 2px;
`;

const TrialFeatureGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TrialSectionTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
  text-transform: uppercase;
`;

const TrialFeatureItem = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

const TrialCheckIcon = styled(IoCheckmark)`
  color: #9a58d1;
  flex-shrink: 0;
  height: 14px;
  width: 14px;
`;

const TrialFeatureText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12px;
  font-weight: 500;

  strong {
    font-weight: 700;
  }
`;

const TrialCTAButton = styled.button`
  background: linear-gradient(135deg, #9a58d1 0%, #7b46a8 100%);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  margin-top: 14px;
  padding: 10px 16px;
  transition: filter 0.2s ease;
  width: 100%;

  &:hover {
    filter: brightness(1.1);
  }
`;

export { UpgradeCard };
